// Resources Explorer (F4a) upload queue.
// A module-level singleton (Vue.observable) so the queue survives folder navigation and
// Explorer re-renders inside Resources. Uploads go through the existing store actions
// resources/uploadDocument | uploadImage | uploadVideo (same args as the classic Resources.vue).
import Vue from 'vue'
import axios from 'axios'
import { checkUploadSize, describeUploadError, notifyUploadLimit } from '@/utils/uploadLimits'
import { createConflictDecider, resolveConflicts, uniqueName } from './fileVersions'

export var UPLOAD_CONCURRENCY = 3
var REFRESH_DEBOUNCE_MS = 700
var GENERIC_UPLOAD_FAILURE = 'Upload failed. Please try again.'
export var SIZE_LIMIT_LABEL = 'Too large for this test environment (max 4.5 MB)'
var SYSTEM_FILES = { '.ds_store': true, 'thumbs.db': true, 'desktop.ini': true }

var state = Vue.observable({ items: [] })

var store = null
var acceptedTypes = null // [{ type, ext, class, color }] from resources/getAcceptedFileTypes
var cancelSources = {} // item id -> axios CancelToken source (kept out of reactive state)
var listeners = []
var refreshTimers = {}
var nextId = 1
var unloadInstalled = false
// F4b: UI hooks registered by the Explorer
var conflictPrompter = null // fn(conflict, remaining) -> Promise<{ choice, applyAll }>
var knownNamesProvider = null // fn(folderId) -> [names already in that folder]
var promptChain = Promise.resolve() // one conflict dialog at a time across folders / batches

export var MODE_LABELS = { version: 'New version', replace: 'Replaced' }

export var uploadQueueState = state

// ---------- type classification (same table + rule as the classic Resources.vue) ----------

// Resources.vue loadData(): getAcceptedFileTypes(), type 1 = document, 2 = image, 3 = video.
// FileUploadModal validates with the vee-validate `ext:` rule against those extensions.
export function loadAcceptedTypes (s) {
  if (s) store = s
  if (acceptedTypes) return Promise.resolve(acceptedTypes)
  return store.dispatch('resources/getAcceptedFileTypes').then(function (types) {
    acceptedTypes = types || []
    return acceptedTypes
  })
}

export function acceptedTypeList () {
  return acceptedTypes || []
}

// Same extension extraction as Resources.vue getCssClassForFile: text after the last '.', lowercased.
function extensionOf (name) {
  return String(name || '').split('.').pop().toLowerCase()
}

function typeEntry (name) {
  var n = String(name || '')
  if (n.indexOf('.') === -1) return null
  var ext = extensionOf(n)
  return acceptedTypeList().filter(function (t) { return t.ext === ext })[0] || null
}

// 'document' | 'image' | 'video' | null (not allowed)
export function classifyFile (name) {
  var t = typeEntry(name)
  if (!t) return null
  if (t.type === 1) return 'document'
  if (t.type === 2) return 'image'
  if (t.type === 3) return 'video'
  return null
}

export function fileIconFor (name) {
  var t = typeEntry(name)
  return t ? { cls: t.class, color: t.color } : { cls: 'fas fa-file', color: 'rgb(92, 92, 92)' }
}

// Comma list for <input accept>: ".pdf,.xlsx,..."
export function acceptAttribute () {
  return acceptedTypeList().map(function (t) { return '.' + t.ext }).join(',')
}

// Hidden / OS metadata files (and anything inside a hidden folder) are skipped silently.
export function isHiddenOrSystem (relativePath) {
  var segs = String(relativePath || '').split('/').filter(function (s) { return !!s })
  return segs.some(function (s) {
    return s.charAt(0) === '.' || SYSTEM_FILES[s.toLowerCase()] === true
  })
}

// ---------- F4b: name conflicts ----------

// fn(conflict, remaining) -> Promise<{ choice: 'keep'|'replace'|'version'|'skip', applyAll }>; null to unregister
export function setConflictPrompter (fn) {
  conflictPrompter = typeof fn === 'function' ? fn : null
}

// fn(folderId) -> array of file names known to be in the folder (used for "Keep both" naming)
export function setKnownNamesProvider (fn) {
  knownNamesProvider = typeof fn === 'function' ? fn : null
}

function askConflict (conflict, remaining) {
  // Without a dialog (Explorer not mounted) fall back to the non-destructive choice
  if (!conflictPrompter) return Promise.resolve({ choice: 'keep', applyAll: false })
  return conflictPrompter(conflict, remaining)
}

function knownNames (folderId) {
  if (!knownNamesProvider) return []
  try {
    return knownNamesProvider(folderId) || []
  } catch (e) {
    return []
  }
}

function release (items) {
  items.forEach(function (it) { it.needsCheck = false })
  pump()
}

// Checks one target folder's queued items against the server and applies the user's choices.
// batch: { decider } shared by all folders of one enqueue call.
function checkConflicts (folderId, propertyId, batch) {
  var items = state.items.filter(function (it) {
    return it.needsCheck && it.status === 'queued' && it.destFolderId === folderId && it.batch === batch
  })
  if (items.length === 0) return Promise.resolve()
  var names = []
  items.forEach(function (it) {
    if (names.indexOf(it.fileName) === -1) names.push(it.fileName)
  })
  return store.dispatch('resources/getNameConflicts', { propertyId: propertyId, folderId: folderId, names: names })
    .then(function (r) { return (r && r.conflicts) || [] }, function () {
      // Check unavailable: upload as before (the server keeps both files)
      return []
    })
    .then(function (conflicts) {
      if (conflicts.length === 0) {
        release(items)
        return null
      }
      var run = promptChain.then(function () {
        return resolveConflicts(conflicts, batch.decider, askConflict)
      })
      promptChain = run.then(function () {}, function () {})
      return run.then(function (results) {
        applyConflictChoices(folderId, items, results)
      }, function () {
        release(items)
      })
    })
}

function applyConflictChoices (folderId, items, results) {
  var byName = {}
  var taken = knownNames(folderId).slice()
  results.forEach(function (x) {
    byName[String(x.conflict.name || '').toLowerCase()] = x
    taken.push(x.conflict.name, x.conflict.existingName, x.conflict.existingFileName)
  })
  items.forEach(function (it) { taken.push(it.fileName) })
  items.forEach(function (it) {
    var x = byName[String(it.fileName).toLowerCase()]
    if (!x || it.status !== 'queued') return
    if (x.choice === 'skip') {
      it.status = 'canceled'
      it.error = ''
    } else if (x.choice === 'keep') {
      it.fileName = uniqueName(it.fileName, taken)
      taken.push(it.fileName)
    } else {
      it.mode = x.choice
      it.targetFileId = x.conflict.fileId
    }
  })
  release(items)
}

// ---------- queue state helpers ----------

export function isActive (item) {
  return item.status === 'queued' || item.status === 'uploading'
}

export function activeCount () {
  return state.items.filter(isActive).length
}

export function hasActiveUploads () {
  return activeCount() > 0
}

// Explorer registers fn(folderId) to refresh folders when uploads finish / folders get created.
export function onFolderChanged (fn) {
  listeners.push(fn)
  return function () {
    listeners = listeners.filter(function (l) { return l !== fn })
  }
}

function notifyFolder (folderId) {
  if (!folderId) return
  var key = String(folderId)
  if (refreshTimers[key]) clearTimeout(refreshTimers[key])
  refreshTimers[key] = setTimeout(function () {
    delete refreshTimers[key]
    listeners.slice().forEach(function (fn) { fn(key) })
  }, REFRESH_DEBOUNCE_MS)
}

function installUnloadWarning () {
  if (unloadInstalled || typeof window === 'undefined') return
  unloadInstalled = true
  window.addEventListener('beforeunload', function (e) {
    if (!hasActiveUploads()) return undefined
    var msg = 'Uploads are still in progress. Leave and cancel them?'
    e.preventDefault()
    e.returnValue = msg
    return msg
  })
}

function failureMessage (e, file) {
  var sizeMessage = describeUploadError(e, file)
  if (sizeMessage) return sizeMessage
  var data = e && e.response && e.response.data
  if (data && typeof data === 'object' && data.failureReason) return String(data.failureReason)
  if (data && typeof data === 'object' && data.message) return String(data.message)
  if (typeof data === 'string' && data.trim() && data.length < 300 && data.indexOf('<') === -1) return data.trim()
  if (e && e.response && e.response.status === 413) return 'The file is too large.'
  if (e && e.message) return e.message
  return GENERIC_UPLOAD_FAILURE
}

// ---------- reading picked / dropped files ----------

// <input type=file> (incl. webkitdirectory) -> [{ file, relativePath }]
export function entriesFromFileList (fileList) {
  var out = []
  for (var i = 0; i < (fileList ? fileList.length : 0); i++) {
    var f = fileList[i]
    out.push({ file: f, relativePath: f.webkitRelativePath || f.name })
  }
  return out
}

function readAllEntries (reader) {
  // readEntries returns batches (Chrome: max 100); call until it yields an empty batch
  var all = []
  return new Promise(function (resolve) {
    function read () {
      reader.readEntries(function (batch) {
        if (!batch || batch.length === 0) {
          resolve(all)
          return
        }
        for (var i = 0; i < batch.length; i++) all.push(batch[i])
        read()
      }, function () { resolve(all) })
    }
    read()
  })
}

function walkEntry (entry, prefix, out) {
  if (!entry) return Promise.resolve()
  if (entry.isFile) {
    return new Promise(function (resolve) {
      entry.file(function (f) {
        out.push({ file: f, relativePath: prefix + f.name })
        resolve()
      }, function () { resolve() })
    })
  }
  if (entry.isDirectory) {
    var dirPrefix = prefix + entry.name + '/'
    return readAllEntries(entry.createReader()).then(function (children) {
      var i = 0
      function next () {
        if (i >= children.length) return Promise.resolve()
        return walkEntry(children[i++], dirPrefix, out).then(next)
      }
      return next()
    })
  }
  return Promise.resolve()
}

// Must be called synchronously inside the drop handler (DataTransfer is cleared afterwards).
// Resolves to [{ file, relativePath }] with directories walked recursively.
export function entriesFromDrop (dataTransfer) {
  var roots = []
  var plain = []
  var items = dataTransfer && dataTransfer.items
  if (items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind !== 'file') continue
      var entry = items[i].webkitGetAsEntry()
      if (entry) {
        roots.push(entry)
      } else {
        var f = items[i].getAsFile()
        if (f) plain.push({ file: f, relativePath: f.name })
      }
    }
  } else {
    plain = entriesFromFileList(dataTransfer ? dataTransfer.files : null)
  }
  var out = []
  var j = 0
  function next () {
    if (j >= roots.length) return Promise.resolve()
    return walkEntry(roots[j++], '', out).then(next)
  }
  return next().then(function () { return out.concat(plain) })
}

export function isFileDrag (e) {
  var types = e && e.dataTransfer ? e.dataTransfer.types : null
  if (!types) return false
  for (var i = 0; i < types.length; i++) {
    if (types[i] === 'Files') return true
  }
  return false
}

// ---------- adding files ----------

// dest: { id, resourceType, propertyId (null for company), label }
// entries: [{ file, relativePath }] relativePath includes the file name ("Top/sub/a.pdf" or "a.pdf").
// Returns { added, skippedTypes: [names] }.
export function enqueueFiles (s, dest, entries) {
  if (s) store = s
  installUnloadWarning()
  var skippedTypes = []
  var accepted = []
  entries.forEach(function (en) {
    var rel = String(en.relativePath || en.file.name).replace(/^\/+/, '')
    if (isHiddenOrSystem(rel)) return
    var kind = classifyFile(en.file.name)
    if (!kind) {
      skippedTypes.push(en.file.name)
      return
    }
    var cut = rel.lastIndexOf('/')
    accepted.push({ file: en.file, kind: kind, relativePath: rel, dir: cut === -1 ? '' : rel.slice(0, cut) })
  })

  var sizeNotice = null
  var batch = { decider: createConflictDecider() }
  var newItems = accepted.map(function (a) {
    var sizeMessage = checkUploadSize(a.file)
    if (sizeMessage && !sizeNotice) sizeNotice = sizeMessage
    var thumbUrl = null
    if (!sizeMessage && a.kind === 'image' && typeof URL !== 'undefined' && URL.createObjectURL) {
      try { thumbUrl = URL.createObjectURL(a.file) } catch (e) { thumbUrl = null }
    }
    return {
      id: 'up' + (nextId++),
      file: a.file,
      kind: a.kind,
      fileName: a.file.name,
      relativePath: a.relativePath,
      dir: a.dir,
      destFolderId: a.dir ? null : String(dest.id),
      destResourceType: dest.resourceType,
      destResourceId: dest.propertyId === undefined ? null : dest.propertyId,
      destLabel: dest.label + (a.dir ? ' / ' + a.dir : ''),
      // Folder-path uploads: the base folder the relative dir is created under
      baseFolderId: String(dest.id),
      needsFolder: !sizeMessage && !!a.dir,
      // Rejected up front by the environment size limit: never sent, cannot be retried
      status: sizeMessage ? 'failed' : 'queued',
      progress: 0,
      error: sizeMessage ? SIZE_LIMIT_LABEL : '',
      errorDetail: sizeMessage || '',
      noRetry: !!sizeMessage,
      thumbUrl: thumbUrl,
      // F4b: name-conflict check before starting; mode 'new' | 'version' | 'replace'
      needsCheck: !sizeMessage,
      batch: batch,
      mode: 'new',
      targetFileId: null
    }
  })
  newItems.forEach(function (it) { state.items.push(it) })

  var dirs = []
  newItems.forEach(function (it) {
    if (it.needsFolder && dirs.indexOf(it.dir) === -1) dirs.push(it.dir)
  })
  // Top-level files are checked for name conflicts first; folder files wait for their folder ids
  checkConflicts(String(dest.id), destPropertyId(dest), batch)
  if (dirs.length > 0) resolveFolders(dest, dirs, batch)
  if (sizeNotice) {
    try { notifyUploadLimit(sizeNotice) } catch (e) { /* toast plugin unavailable */ }
  }
  return { added: newItems.length, skippedTypes: skippedTypes }
}

function destPropertyId (dest) {
  return dest.propertyId === undefined ? null : dest.propertyId
}

// Sequentially create each relative dir under dest (resources/createFolderPath reuses same-name folders).
// Reused existing folders are checked for name conflicts; newly created ones have none.
function resolveFolders (dest, dirs, batch) {
  var cache = {}
  dirs.sort()
  var i = 0
  function next () {
    if (i >= dirs.length) {
      notifyFolder(dest.id)
      pump()
      return Promise.resolve()
    }
    var dir = dirs[i++]
    var path = dir.split('/').map(function (seg) { return seg.trim() }).join('/')
    var bad = path.split('/').some(function (seg) { return !seg })
    var req = bad
      ? Promise.resolve({ success: false, failureReason: 'Invalid folder name in "' + dir + '"' })
      : store.dispatch('resources/createFolderPath', {
        propertyId: dest.propertyId === undefined ? null : dest.propertyId,
        parentId: dest.id,
        path: path
      })
    return req.then(function (r) {
      var ok = r && r.success === true && r.folder && r.folder.id !== undefined && r.folder.id !== null
      if (ok) cache[dir] = String(r.folder.id)
      var created = ok && (r.createdFolderIds || []).some(function (id) { return String(id) === cache[dir] })
      applyFolder(dest, dir, ok ? cache[dir] : null, ok ? '' : ((r && r.failureReason) || 'The folder could not be created.'), created)
      if (ok && !created && batch) checkConflicts(cache[dir], destPropertyId(dest), batch)
      // Start uploads for folders already resolved while the rest are being created
      pump()
      return next()
    }, function () {
      applyFolder(dest, dir, null, 'The folder could not be created.')
      return next()
    })
  }
  return next()
}

function applyFolder (dest, dir, folderId, reason, created) {
  state.items.forEach(function (it) {
    if (!it.needsFolder || it.dir !== dir || it.baseFolderId !== String(dest.id)) return
    if (it.status !== 'queued') return
    it.needsFolder = false
    if (folderId) {
      it.destFolderId = folderId
      if (created || !it.batch) it.needsCheck = false
    } else {
      it.status = 'failed'
      it.error = reason
      it.retryDest = dest
    }
  })
}

// ---------- running ----------

function pump () {
  var running = state.items.filter(function (it) { return it.status === 'uploading' }).length
  for (var i = 0; i < state.items.length && running < UPLOAD_CONCURRENCY; i++) {
    var it = state.items[i]
    if (it.status === 'queued' && !it.needsFolder && !it.needsCheck && it.destFolderId) {
      running++
      start(it)
    }
  }
}

function start (item) {
  var source = axios.CancelToken.source()
  cancelSources[item.id] = source
  item.status = 'uploading'
  item.progress = 0
  item.error = ''
  var onUploadProgress = function (e) {
    if (item.status !== 'uploading' || !e || !e.total) return
    // Keep 100 for when the server has answered
    item.progress = Math.min(99, Math.round((e.loaded / e.total) * 100))
  }
  var request
  if ((item.mode === 'version' || item.mode === 'replace') && item.targetFileId !== null && item.targetFileId !== undefined) {
    // F4b: new version of / replacement for the existing file with the same name
    request = store.dispatch('resources/' + (item.mode === 'version' ? 'uploadFileVersion' : 'replaceFile'), {
      file: item.file,
      fileName: item.fileName,
      fileId: item.targetFileId,
      propertyId: item.destResourceId,
      onUploadProgress: onUploadProgress,
      cancelToken: source.token
    })
  } else {
    var action = item.kind === 'image' ? 'uploadImage' : (item.kind === 'video' ? 'uploadVideo' : 'uploadDocument')
    // Same args the classic Resources.vue / FileUploadModal pass (name = file name, no note, no event)
    request = store.dispatch('resources/' + action, {
      file: item.file,
      fileName: item.fileName,
      name: item.fileName,
      note: null,
      folderId: item.destFolderId,
      folderType: item.destResourceType,
      propertyId: item.destResourceId,
      eventId: undefined,
      onUploadProgress: onUploadProgress,
      cancelToken: source.token,
      throwOnError: true
    })
  }
  request.then(function (result) {
    delete cancelSources[item.id]
    if (item.status !== 'uploading') return
    var data = result && result.data
    if (data && typeof data === 'object' && data.success === false) {
      item.status = 'failed'
      item.error = data.failureReason || GENERIC_UPLOAD_FAILURE
    } else {
      item.status = 'done'
      item.progress = 100
      notifyFolder(item.destFolderId)
    }
  }, function (e) {
    delete cancelSources[item.id]
    if (axios.isCancel(e) || item.status === 'canceled') {
      item.status = 'canceled'
    } else if (item.status === 'uploading') {
      item.status = 'failed'
      var sizeMessage = describeUploadError(e, item.file)
      if (sizeMessage) {
        item.error = SIZE_LIMIT_LABEL
        item.errorDetail = sizeMessage
      } else {
        item.error = failureMessage(e, item.file)
      }
    }
  }).then(function () {
    pump()
  })
}

export function cancelItem (item) {
  if (!isActive(item)) return
  var wasUploading = item.status === 'uploading'
  item.status = 'canceled'
  item.error = ''
  if (wasUploading && cancelSources[item.id]) {
    cancelSources[item.id].cancel('Canceled')
    delete cancelSources[item.id]
  }
  pump()
}

export function cancelAll () {
  state.items.slice().forEach(function (it) { cancelItem(it) })
}

export function retryItem (item) {
  if (item.status !== 'failed' || item.noRetry) return
  item.error = ''
  item.errorDetail = ''
  item.progress = 0
  item.status = 'queued'
  if (!item.destFolderId && item.retryDest) {
    // Folder creation failed earlier: try creating the path again
    var dest = item.retryDest
    item.retryDest = null
    item.needsFolder = true
    resolveFolders(dest, [item.dir], item.batch)
    return
  }
  pump()
}

// Removes finished (done / failed / canceled) items and frees their thumbnails.
export function clearFinished () {
  var keep = []
  state.items.forEach(function (it) {
    if (isActive(it)) {
      keep.push(it)
    } else if (it.thumbUrl) {
      try { URL.revokeObjectURL(it.thumbUrl) } catch (e) { /* ignore */ }
    }
  })
  state.items = keep
}
